import { Component, OnDestroy, OnInit, inject, ElementRef, ViewChild } from '@angular/core';
import { AppViewportScroller } from '../../../core/services';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';

import { AdminService } from '../services/admin.service';
import { AdminFilterField, countActiveFilters, openAdminFilters } from '../filters-sheet/admin-filters-sheet';
import { ModalService } from '../../../shared/modal';
import { isPhoneViewport } from '../../../core/utils/phone';
import { AdminTransactionRow, AdminTransactionTotals } from '../models/admin-transaction.model';
import { AdminPagination } from '../models/admin-pagination.model';
import { dateRangeLabel, filterLabel } from '../utils/filter-label';

const PER_PAGE = 50;

const KIND_LABELS: Record<string, string> = {
  topup: 'Top-ups',
  grant: 'Grants',
  adjustment: 'Adjustments',
  refund: 'Refunds',
};

/** Filters live in the URL (?q=&kind=&from=&to=&page=), same convention as
 *  Calls (and how Organization Detail's "Transactions" cross-link lands
 *  here pre-filtered). `q` filters by organization (slug, id, or name)
 *  confirmed against the real API, not the written contract, which named
 *  this param `organization` instead. */
@Component({
  selector: 'app-admin-transactions',
  standalone: false,
  templateUrl: './admin-transactions.html',
  styleUrl: './admin-transactions.scss',
})
export class AdminTransactions implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();
  private readonly reload$ = new Subject<void>();

  rows: AdminTransactionRow[] = [];
  /** Covers the whole filtered list (per the contract), not just the page
   *  re-fetched alongside the rows whenever a filter changes. */
  totals: AdminTransactionTotals | null = null;
  pagination: AdminPagination | null = null;
  isLoading = false;
  /** Until the first response: the Totals card shows a skeleton meanwhile. */
  hasLoadedOnce = false;

  q = '';
  kind = '';
  from = '';
  to = '';
  page = 1;

  /** Phones get a Filters button and sheet instead of the inline filters. */
  readonly phone = isPhoneViewport();
  private readonly modals = inject(ModalService);

  private readonly filterFields: AdminFilterField[] = [
    { kind: 'segments', key: 'kind', label: 'Kind', options: [{ value: '', label: 'All' }, { value: 'topup', label: 'Top-up' }, { value: 'grant', label: 'Grant' }, { value: 'adjustment', label: 'Adjustment' }, { value: 'refund', label: 'Refund' }] },
    { kind: 'dates', fromKey: 'from', toKey: 'to', label: 'Date' },
  ];
  private readonly filterDefaults = { kind: '', from: '', to: '' };

  private get filterValues(): Record<string, string> {
    return { kind: this.kind, from: this.from, to: this.to };
  }

  /** Phones: every filter but the search, in a sheet; applied in one go. */
  openFilters(): void {
    openAdminFilters(this.modals, { fields: this.filterFields, values: this.filterValues, defaults: this.filterDefaults }, v => {
      this.kind = v['kind'];
      this.from = v['from'];
      this.to = v['to'];
      this.applyFilters();
    });
  }

  /** Badge on the phone Filters button: how many filters are set. */
  get activeFilterCount(): number {
    return countActiveFilters(this.filterValues, this.filterDefaults);
  }

  constructor(
    private adminService: AdminService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.q = params.get('q') ?? '';
    this.kind = params.get('kind') ?? '';
    this.from = params.get('from') ?? '';
    this.to = params.get('to') ?? '';
    this.page = Number(params.get('page')) || 1;

    this.reload$.pipe(
      debounceTime(250),
      switchMap(() => {
        this.isLoading = true;
        return this.adminService.getTransactions({
          q: this.q.trim() || undefined,
          kind: this.kind || undefined,
          from: this.from ? `${this.from}T00:00:00Z` : undefined,
          to: this.to ? `${this.to}T23:59:59Z` : undefined,
          page: this.page,
          perPage: PER_PAGE,
        });
      }),
      takeUntil(this._destroy$),
    ).subscribe(({ rows, totals, pagination }) => {
      this.rows = rows;
      this.totals = totals;
      this.pagination = pagination;
      this.isLoading = false;
      this.hasLoadedOnce = true;
    });

    this.reload$.next();
  }

  /** Credited plus debited (debits arrive negative). */
  get netCents(): number {
    return this.totals ? this.totals.creditedCents + this.totals.debitedCents : 0;
  }

  /** "1,234.00" / "−13.00": a true minus sign rather than the hyphen
   *  toLocaleString gives, so negative totals read as money. */
  signed(cents: number): string {
    const abs = Math.abs(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return cents < 0 ? `−${abs}` : abs;
  }

  private applyFilters(): void {
    this.page = 1;
    this.syncUrl();
  }

  onQueryChange(value: string): void {
    this.q = value;
    this.applyFilters();
  }

  onKindChange(value: string): void {
    this.kind = value;
    this.applyFilters();
  }

  onFromChange(value: string): void {
    this.from = value;
    this.applyFilters();
  }

  onToChange(value: string): void {
    this.to = value;
    this.applyFilters();
  }

  @ViewChild('listCard') private listCard?: ElementRef<HTMLElement>;
  private readonly scroller = inject(AppViewportScroller);

  goToPage(page: number): void {
    if (page < 1 || (this.pagination && page > this.pagination.pages)) return;
    this.page = page;
    // Next/Previous land on the top of the list, not the top of the page.
    this.scroller.overrideNextScroll(() => this.listCard?.nativeElement.scrollIntoView({ block: 'start' }));
    this.syncUrl();
  }

  clearFilters(): void {
    this.q = '';
    this.kind = '';
    this.from = '';
    this.to = '';
    this.applyFilters();
  }

  get hasFilters(): boolean {
    return !!(this.q || this.kind || this.from || this.to);
  }

  get totalsFilterLabel(): string | null {
    return filterLabel([
      this.q.trim() && `“${this.q.trim()}”`,
      this.kind && KIND_LABELS[this.kind],
      dateRangeLabel(this.from, this.to),
    ]);
  }

  private syncUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        q: this.q.trim() || null,
        kind: this.kind || null,
        from: this.from || null,
        to: this.to || null,
        page: this.page > 1 ? this.page : null,
      },
      replaceUrl: true,
    });
    this.reload$.next();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
